import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, formatApiError } from '../contexts/AuthContext';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { AUTH } from '../constants/testIds';

export default function Auth({ mode = 'login' }) {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) await login(email, password);
      else await register(email, password, fullName);
      toast.success('Admission granted.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Authentication failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[85vh] grid grid-cols-1 md:grid-cols-2">
      <div className="hidden md:block relative bg-[#0A0A0A] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1613977257363-707ba9348227?w=1920"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#050505] via-[#050505]/70 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]">Sovereign · Master Tier</div>
          <div>
            <p className="font-display text-3xl md:text-4xl italic text-[#F5F5F5] leading-tight mb-6 max-w-md">
              "Admission is identity-anchored. Capital is admiral-cleared."
            </p>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#A3A3A3]">— Mercy</div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-md">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">
            {isLogin ? 'Sign In' : 'Create Account'}
          </div>
          <h1 className="text-4xl md:text-5xl font-extralight leading-tight tracking-tight mb-10">
            {isLogin ? <>Welcome<br /><span className="font-display italic text-[#D4AF37]">aboard.</span></> :
              <>Begin your<br /><span className="font-display italic text-[#D4AF37]">boarding.</span></>}
          </h1>

          <form onSubmit={submit} className="space-y-8">
            {!isLogin && (
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Full Name</label>
                <input data-testid={AUTH.fullNameInput} className="input-sovereign" value={fullName}
                  onChange={(e) => setFullName(e.target.value)} required />
              </div>
            )}
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Email</label>
              <input data-testid={AUTH.emailInput} type="email" className="input-sovereign" value={email}
                onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Password</label>
              <input data-testid={AUTH.passwordInput} type="password" className="input-sovereign" value={password}
                onChange={(e) => setPassword(e.target.value)} required minLength={8} />
            </div>

            <button type="submit" disabled={loading} data-testid={AUTH.submitBtn} className="btn-sovereign w-full">
              {loading ? <><Loader2 size={16} className="animate-spin" /> Processing</> : <>{isLogin ? 'Enter Command' : 'Create Account'} <ArrowUpRight size={16} strokeWidth={1.5} /></>}
            </button>
          </form>

          <div className="mt-12 pt-8 border-t border-[#D4AF37]/15">
            <button onClick={() => setIsLogin(!isLogin)} data-testid={AUTH.switchModeBtn}
              className="text-[11px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:text-[#D4AF37] transition-colors">
              {isLogin ? 'No account? Begin boarding' : 'Already aboard? Sign in'}
            </button>
            <div className="mt-4">
              <Link to="/" className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:text-[#D4AF37] transition-colors">
                ← Return to bridge
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
